import { useCallback, useEffect, useMemo, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Drawer } from '../components/Drawer';
import { translateVisibleNode, useI18n, useVisibleCopy } from '../i18n';
import type { RedactionOptions } from '../lib/redaction';
import {
  commitConfigDraftSave,
  createConfigDraftState,
  diffJsonValues,
  discardConfigDraftChanges,
  isConfigDraftDirty,
  redactedPrettyConfigJson,
  updateConfigDraftField,
  updateConfigDraftValues,
  type ConfigDraftInput,
  type ConfigDraftSource,
  type ConfigDraftState,
  type JsonDiffRow,
  stableConfigStringify,
} from './model';

export type ConfigEditorLabels = {
  saveDraft?: string;
  discardChanges?: string;
  draftJson?: string;
  saved?: string;
  unsaved?: string;
  sourceLabel?: string;
};

export type ConfigSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type ConfigSegmentOption = ConfigSelectOption;

export type ConfigKeyValueEntry = {
  id?: string;
  key: string;
  value: string;
};

export type SecretReferenceOption = {
  id: string;
  label: string;
  description?: string;
};

export function useConfigDraftEditor<TValue extends Record<string, unknown>>({
  onSaveDraft,
  ...input
}: ConfigDraftInput<TValue> & {
  onSaveDraft?: (values: TValue) => Promise<TValue | void> | TValue | void;
}) {
  const [state, setState] = useState<ConfigDraftState<TValue>>(() => createConfigDraftState(input));
  const inputSignature = stableConfigStringify(input);
  const dirty = isConfigDraftDirty(state);

  useEffect(() => {
    setState(currentState => (isConfigDraftDirty(currentState) ? currentState : createConfigDraftState(input)));
  }, [inputSignature]);

  const setValues = useCallback((values: TValue) => {
    setState(currentState => updateConfigDraftValues(currentState, values));
  }, []);

  const updateValues = useCallback((updater: (values: TValue) => TValue) => {
    setState(currentState => updateConfigDraftValues(currentState, updater(currentState.values)));
  }, []);

  const setField = useCallback(<TKey extends keyof TValue>(field: TKey, value: TValue[TKey]) => {
    setState(currentState => updateConfigDraftField(currentState, field, value));
  }, []);

  const discardChanges = useCallback(() => {
    setState(currentState => discardConfigDraftChanges(currentState));
  }, []);

  const saveDraft = useCallback(async () => {
    const saveResult = await onSaveDraft?.(state.values);
    const savedValues = saveResult ?? state.values;
    setState(currentState => commitConfigDraftSave(currentState, savedValues));
    return savedValues;
  }, [onSaveDraft, state.values]);

  const diffRows = useMemo(() => diffJsonValues(state.publishedValue, state.values), [state.publishedValue, state.values]);

  return {
    state,
    values: state.values,
    baseline: state.baseline,
    publishedValue: state.publishedValue,
    source: state.source,
    dirty,
    diffRows,
    setValues,
    updateValues,
    setField,
    discardChanges,
    saveDraft,
  };
}

export function ConfigEditorShell({
  title,
  description,
  source,
  dirty,
  saving = false,
  editing = true,
  labels,
  children,
  onSaveDraft,
  onDiscardChanges,
  onOpenDraftJson,
}: {
  title: string;
  description?: string;
  source?: ConfigDraftSource;
  dirty: boolean;
  saving?: boolean;
  editing?: boolean;
  labels?: ConfigEditorLabels;
  children: ReactNode;
  onSaveDraft: () => void;
  onDiscardChanges: () => void;
  onOpenDraftJson?: () => void;
}) {
  const copy = normalizeLabels(labels);
  const ui = useVisibleCopy();
  return (
    <section className="config-editor-shell" aria-label={title}>
      <div className="config-editor-header">
        <div>
          <p className="eyebrow">{ui('Config editor foundation')}</p>
          <h2>{ui(title)}</h2>
          {description ? <p>{ui(description)}</p> : null}
          {source ? <small>{ui(copy.sourceLabel)}: {ui(sourceLabel(source))}</small> : null}
        </div>
        <span className={dirty ? 'badge config-editor-status status-dirty' : 'badge config-editor-status'}>{ui(dirty ? copy.unsaved : copy.saved)}</span>
      </div>
      <div className="config-editor-body">{children}</div>
      <div className="config-editor-actions action-row">
        {editing && onOpenDraftJson ? <button className="btn btn-subtle" type="button" onClick={onOpenDraftJson}>{ui(copy.draftJson)}</button> : null}
        <button className="btn btn-subtle" type="button" onClick={onDiscardChanges} disabled={!dirty || saving}>{ui(copy.discardChanges)}</button>
        <button className="btn btn-primary" type="button" onClick={onSaveDraft} disabled={!dirty || saving}>{ui(saving ? 'Saving…' : copy.saveDraft)}</button>
      </div>
    </section>
  );
}

export function ConfigEditorSection({ title, description, children, actions }: { title: string; description?: string; children: ReactNode; actions?: ReactNode }) {
  const ui = useVisibleCopy();

  return (
    <section className="config-editor-section">
      <div className="panel-header">
        <div>
          <h3>{ui(title)}</h3>
          {description ? <p>{ui(description)}</p> : null}
        </div>
        {actions ? <div className="action-row">{actions}</div> : null}
      </div>
      <div className="config-editor-section-body">{children}</div>
    </section>
  );
}

export function ConfigCard({ title, description, children }: { title?: string; description?: string; children: ReactNode }) {
  const ui = useVisibleCopy();

  return (
    <article className="config-card">
      {title || description ? (
        <div className="config-card-header">
          {title ? <h4>{ui(title)}</h4> : null}
          {description ? <p>{ui(description)}</p> : null}
        </div>
      ) : null}
      {children}
    </article>
  );
}

export function ConfigField({
  id,
  label,
  required = false,
  helperText,
  error,
  children,
}: {
  id?: string;
  label: string;
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={error ? 'config-field has-error' : 'config-field'}>
      <ConfigFieldLabel htmlFor={id} label={label} required={required} />
      {children}
      {helperText ? <ConfigHelperText>{helperText}</ConfigHelperText> : null}
      {error ? <ConfigValidationError>{error}</ConfigValidationError> : null}
    </div>
  );
}

export function ConfigFieldLabel({ htmlFor, label, required = false }: { htmlFor?: string; label: string; required?: boolean }) {
  const ui = useVisibleCopy();

  return (
    <label className="config-field-label" htmlFor={htmlFor}>
      <span>{ui(label)}</span>
      {required ? <span className="required-marker" aria-label={ui('required')}>*</span> : null}
    </label>
  );
}

export function ConfigHelperText({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  return <p className="config-helper">{translateVisibleNode(children, locale)}</p>;
}

function VisibleCopyText({ value }: { value: string }) {
  const ui = useVisibleCopy();
  return <>{ui(value)}</>;
}

export function ConfigValidationError({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  return <p className="config-error" role="alert">{translateVisibleNode(children, locale)}</p>;
}

export function ConfigTextInput({
  value,
  onChange,
  type = 'text',
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'value'> & {
  value: string;
  onChange: (value: string) => void;
  type?: 'email' | 'password' | 'search' | 'text' | 'url';
}) {
  const ui = useVisibleCopy();
  const localizedProps = localizeInputProps(props, ui);
  return <input {...localizedProps} type={type} value={value} onChange={event => onChange(event.currentTarget.value)} />;
}

export function ConfigTextarea({
  value,
  onChange,
  ...props
}: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'> & {
  value: string;
  onChange: (value: string) => void;
}) {
  const ui = useVisibleCopy();
  const localizedProps = localizeInputProps(props, ui);
  return <textarea {...localizedProps} value={value} onChange={event => onChange(event.currentTarget.value)} />;
}

export function ConfigSelect({
  value,
  onChange,
  options,
  placeholder,
  ...props
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> & {
  value: string;
  onChange: (value: string) => void;
  options: ConfigSelectOption[];
  placeholder?: string;
}) {
  const ui = useVisibleCopy();
  const localizedProps = localizeInputProps(props, ui);
  return (
    <select {...localizedProps} value={value} onChange={event => onChange(event.currentTarget.value)}>
      {placeholder ? <option value="">{ui(placeholder)}</option> : null}
      {options.map(option => <option key={option.value} value={option.value} disabled={option.disabled}>{ui(option.label)}</option>)}
    </select>
  );
}

export function ConfigToggle({
  checked,
  onChange,
  label,
  onLabel = 'On',
  offLabel = 'Off',
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  onLabel?: string;
  offLabel?: string;
  disabled?: boolean;
}) {
  const ui = useVisibleCopy();

  return (
    <button
      aria-checked={checked}
      aria-label={ui(label)}
      className={checked ? 'config-toggle active' : 'config-toggle'}
      disabled={disabled}
      role="switch"
      type="button"
      onClick={() => onChange(!checked)}
    >
      <span>{ui(checked ? onLabel : offLabel)}</span>
    </button>
  );
}

export function ConfigCheckbox({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  const ui = useVisibleCopy();

  return (
    <label className="config-checkbox">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={event => onChange(event.currentTarget.checked)} />
      <span>{ui(label)}</span>
    </label>
  );
}

export function ConfigSegmentedControl({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  options: ConfigSegmentOption[];
  label: string;
}) {
  const ui = useVisibleCopy();

  return (
    <div className="config-segmented" role="group" aria-label={ui(label)}>
      {options.map(option => (
        <button
          key={option.value}
          aria-pressed={option.value === value}
          className={option.value === value ? 'active' : undefined}
          disabled={option.disabled}
          type="button"
          onClick={() => onChange(option.value)}
        >
          {ui(option.label)}
        </button>
      ))}
    </div>
  );
}

export function ConfigRepeatableRows<TItem>({
  items,
  onChange,
  createItem,
  renderItem,
  addLabel = 'Add row',
  getItemKey,
  disabled = false,
}: {
  items: TItem[];
  onChange: (items: TItem[]) => void;
  createItem: () => TItem;
  renderItem: (context: { item: TItem; index: number; updateItem: (item: TItem) => void; removeItem: () => void }) => ReactNode;
  addLabel?: string;
  getItemKey?: (item: TItem, index: number) => string | number;
  disabled?: boolean;
}) {
  const updateItemAt = (index: number, item: TItem) => onChange(items.map((currentItem, currentIndex) => (currentIndex === index ? item : currentItem)));
  const removeItemAt = (index: number) => onChange(items.filter((_, currentIndex) => currentIndex !== index));
  return (
    <div className="config-repeatable">
      {items.map((item, index) => (
        <div className="config-repeatable-row" key={getItemKey ? getItemKey(item, index) : String(index)}>
          {renderItem({ item, index, updateItem: nextItem => updateItemAt(index, nextItem), removeItem: () => removeItemAt(index) })}
        </div>
      ))}
      <button className="btn btn-compact btn-subtle" type="button" disabled={disabled} onClick={() => onChange([...items, createItem()])}><VisibleCopyText value={addLabel} /></button>
    </div>
  );
}

export function ConfigKeyValueEditor({
  entries,
  onChange,
  addLabel = 'Add key/value',
  keyLabel = 'Key',
  valueLabel = 'Value',
  removeLabel = 'Remove',
  disabled = false,
}: {
  entries: ConfigKeyValueEntry[];
  onChange: (entries: ConfigKeyValueEntry[]) => void;
  addLabel?: string;
  keyLabel?: string;
  valueLabel?: string;
  removeLabel?: string;
  disabled?: boolean;
}) {
  return (
    <ConfigRepeatableRows
      items={entries}
      onChange={onChange}
      createItem={() => ({ id: nextConfigKeyValueEntryId(), key: '', value: '' })}
      addLabel={addLabel}
      getItemKey={(item, index) => item.id ?? index}
      disabled={disabled}
      renderItem={({ item, index, updateItem, removeItem }) => (
        <div className="config-key-value-row">
          <ConfigTextInput aria-label={`${keyLabel} ${index + 1}`} value={item.key} disabled={disabled} onChange={key => updateItem({ ...item, key })} placeholder={keyLabel} />
          <ConfigTextInput aria-label={`${valueLabel} ${index + 1}`} value={item.value} disabled={disabled} onChange={value => updateItem({ ...item, value })} placeholder={valueLabel} />
          <button className="btn btn-compact btn-subtle" type="button" disabled={disabled} onClick={removeItem}><VisibleCopyText value={removeLabel} /></button>
        </div>
      )}
    />
  );
}

let configKeyValueEntrySequence = 0;

function nextConfigKeyValueEntryId() {
  configKeyValueEntrySequence += 1;
  return `config-kv-${configKeyValueEntrySequence}`;
}

export function SecretRefSelectorPlaceholder({
  value,
  onChange,
  options,
  placeholder = 'Select a secret reference',
  helperText = 'Secret values are never rendered in Console v2; only stable reference IDs are saved.',
}: {
  value: string;
  onChange: (value: string) => void;
  options: SecretReferenceOption[];
  placeholder?: string;
  helperText?: string;
}) {
  const ui = useVisibleCopy();

  return (
    <div className="config-secret-placeholder">
      <ConfigSelect value={value} onChange={onChange} options={options.map(option => ({ value: option.id, label: option.label }))} placeholder={placeholder} />
      <ConfigHelperText>{ui(helperText)}</ConfigHelperText>
    </div>
  );
}

export function ConfigDraftJsonDrawer({
  open,
  onClose,
  draft,
  published,
  editing = true,
  title = 'Draft JSON',
  closeLabel = 'Close',
  diffTitle = 'Draft vs published diff',
  emptyDiffLabel = 'No draft changes compared with the published/current fallback.',
  redactionOptions,
}: {
  open: boolean;
  onClose: () => void;
  draft: unknown;
  published: unknown;
  editing?: boolean;
  title?: string;
  closeLabel?: string;
  diffTitle?: string;
  emptyDiffLabel?: string;
  redactionOptions?: RedactionOptions;
}) {
  const ui = useVisibleCopy();
  const diffRows = useMemo(() => diffJsonValues(published, draft, redactionOptions), [published, draft, redactionOptions]);
  if (!editing) return null;
  return (
    <Drawer title={title} open={open} onClose={onClose} badge="redacted" closeLabel={closeLabel}>
      <div className="page-stack">
        <article className="summary-card">
          <h3>{ui(title)}</h3>
          <pre className="json-preview">{redactedPrettyConfigJson(draft, redactionOptions)}</pre>
        </article>
        <article className="summary-card">
          <h3>{ui(diffTitle)}</h3>
          <ConfigDiffTable rows={diffRows} emptyLabel={emptyDiffLabel} />
        </article>
      </div>
    </Drawer>
  );
}

export function ConfigDiffTable({ rows, emptyLabel }: { rows: JsonDiffRow[]; emptyLabel: string }) {
  const ui = useVisibleCopy();

  return (
    <div className="table-scroll">
      <table className="data-table config-diff-table">
        <thead><tr><th>{ui('Path')}</th><th>{ui('Change')}</th><th>{ui('Published/current')}</th><th>{ui('Draft')}</th></tr></thead>
        <tbody>
          {rows.length ? rows.map(row => (
            <tr key={`${row.path}-${row.kind}`}>
              <td><code>{row.path}</code></td>
              <td><span className={`config-diff-kind kind-${row.kind}`}>{row.kind}</span></td>
              <td><pre>{row.before}</pre></td>
              <td><pre>{row.after}</pre></td>
            </tr>
          )) : <tr><td colSpan={4}>{ui(emptyLabel)}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function localizeInputProps<TProps extends { placeholder?: string; title?: string; 'aria-label'?: string }>(props: TProps, ui: (value: string) => string): TProps {
  return {
    ...props,
    placeholder: typeof props.placeholder === 'string' ? ui(props.placeholder) : props.placeholder,
    title: typeof props.title === 'string' ? ui(props.title) : props.title,
    'aria-label': typeof props['aria-label'] === 'string' ? ui(props['aria-label']) : props['aria-label'],
  };
}

function normalizeLabels(labels: ConfigEditorLabels | undefined): Required<ConfigEditorLabels> {
  return {
    saveDraft: labels?.saveDraft ?? 'Save draft',
    discardChanges: labels?.discardChanges ?? 'Discard changes',
    draftJson: labels?.draftJson ?? 'Draft JSON / diff',
    saved: labels?.saved ?? 'Saved',
    unsaved: labels?.unsaved ?? 'Unsaved changes',
    sourceLabel: labels?.sourceLabel ?? 'Prefill source',
  };
}

function sourceLabel(source: ConfigDraftSource) {
  if (source === 'draft') return 'current draft';
  if (source === 'published') return 'published config';
  if (source === 'current') return 'current runtime config';
  return 'empty defaults';
}
